/* Copyright (c) 2021-2026, NVIDIA CORPORATION.  All rights reserved.
*
* NVIDIA CORPORATION and its licensors retain all intellectual property
* and proprietary rights in and to this software, related documentation
* and any modifications thereto.  Any use, reproduction, disclosure or
* distribution of this software and related documentation without an express
* license agreement from NVIDIA CORPORATION is strictly prohibited.
*/

/** This file was automatically generated. DO NOT MODIFY IT BY HAND. */

/* eslint-disable @typescript-eslint/naming-convention */

export module GalleryTypes {

/** This object was automatically generated by json-schema-to-typescript. DO NOT MODIFY IT BY HAND. */

/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "MontageConfigInfo".
 */
export type MontageConfigInfo = {
    id?: string;
    name?: string;
}[];

export interface Gallery {
    [k: string]: any;
}
/**
 * DRS-sourced information about an application
 *
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "HighlightAppInfo".
 */
export interface HighlightAppInfo {
    /**
     * DRS-sourced short name of the application for the Highlight
     */
    appShortName?: string;
    /**
     * DRS-sourced name of the application for the Highlight
     */
    name?: string;
    /**
     * DRS-sourced profile name of the application for the Highlight
     */
    profileName?: string;
}
/**
 * Group information set by the application
 *
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "HighlightGroupInfo".
 */
export interface HighlightGroupInfo {
    /**
     * Application-sourced group identifier that the Highlight belongs to
     */
    groupId?: string;
    /**
     * Application-sourced tag values for the Highlight
     */
    tags?: number;
    /**
     * Application-sourced significance values for the Highlight
     */
    significance?: number;
}
/**
 * Data for a generated Higlight
 *
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "Highlight".
 */
export interface Highlight {
    /**
     * Internal identifier value of the Highlight
     */
    id?: number;
    /**
     * Application-sourced definition identifier of the Highlight
     */
    definitionId?: string;
    /**
     * Application-sourced name of the Highlight
     */
    name?: string;
    /**
     * Application-sourced group identifier that the Highlight belongs to
     */
    groupId?: string;
    type?: Type;
    /**
     * Fully-quantified path of the Highlight
     */
    path?: string;
    /**
     * Application-sourced tag values for the Highlight
     */
    tags?: number;
    /**
     * Application-sourced significance values for the Highlight
     */
    significance?: number;
    /**
     * Defines if the Highlight is saved to gallery (true), or in the temporary location (false)
     */
    savedToGallery?: boolean;
    /**
     * Length of the video, in seconds (defined only if Highlight type is video, videos less than 1 second report as 0)
     */
    videoLengthSeconds?: number;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "MontageInput".
 */
export interface MontageInput {
    details: details;
    inputFileType: InputFileType;
    /**
     * Path to the EDGE log associated with the input video
     */
    edgeLog?: string;
}
export interface details {
    /**
     * Path to input file
     */
    path: string;
    /**
     * Duration, in milliseconds, of the video or audio file
     */
    duration: number;
    /**
     * Horizontal size of video or image
     */
    width?: number;
    /**
     * Vertical size of video or image
     */
    height?: number;
    /**
     * Label of specific highlight type
     */
    highlightType?: string;
    /**
     * Filter to apply
     */
    filter?: string;
    source?: source;
    transition?: transition;
    /**
     * cmsId of current game
     */
    cmsid?: string;
}
export interface source {
    app?: string;
    /**
     * Monikor for how NV app captured the input file
     */
    method?: string;
}
export interface transition {
    /**
     * Type of transition
     */
    kind?: string;
    /**
     * Speed of transition
     */
    speed?: 0.5 | 1 | 2;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "Storyboard".
 */
export interface Storyboard {
    titleSequence?: titleSequence;
    /**
     * Choose a celebration to use in the story board
     */
    celebration?: string;
    brandNV?: brandNV;
}
export interface titleSequence {
    seqType?: SeqType;
    seqDetails?: seqDetails;
}
export interface seqDetails {
    /**
     * This is the text used in the title sequence
     */
    txtTitle?: string;
    /**
     * This is the Subtitle used in the title sequence
     */
    txtSubtitle?: string;
}
export interface brandNV {
    brandType?: BrandType;
    brandDetails?: brandDetails;
}
export interface brandDetails {
    position?: Position;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "MemeInfo".
 */
export interface MemeInfo {
    /**
     * Text string to be displayed in the top bar
     */
    topBarText: string;
    /**
     * Text string to be displayed in the bottom bar
     */
    bottomBarText: string;
}
/**
 * Data for a generated UGC
 *
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "UGC".
 */
export interface UGC {
    /**
     * Internal identifier value of the UGC
     */
    id?: number;
    /**
     * Fully-quantified path of the UGC
     */
    path?: string;
    fileType?: FileType;
    fileSubType?: SubType;
    fileSource?: Source;
    /**
     * Size of file in kilobytes
     */
    fileSizeKB?: number;
    /**
     * Creation date of the file
     */
    date?: string;
    /**
     * DRS Name embedded in the file
     */
    DRSName?: string;
    /**
     * DRS profile embedded in the file
     */
    DRSProfileName?: string;
    /**
     * CMS ID of the app
     */
    cmsId?: number;
    /**
     * Duration of video file in hh:mm:ss
     */
    duration?: string;
    /**
     * Duration of video file in milliseconds
     */
    msDuration?: number;
    audiotype?: AudioType;
    /**
     * Application-sourced definition identifier of the Highlight
     */
    highlightDefinitionId?: string;
    /**
     * Application-sourced name of the Highlight
     */
    highlightName?: string;
    /**
     * Application-sourced group identifier that the Highlight belongs to
     */
    highlightGroupId?: string;
    /**
     * Application-sourced tag values for the Highlight
     */
    highlightTags?: number;
    /**
     * Application-sourced significance values for the Highlight
     */
    highlightSignificance?: number;
    /**
     * Timestamp of Highlight Media File
     */
    eventTimeStamp?: string;
    memeInfo?: MemeInfo;
    /**
     * Codec used for file
     */
    codecType?: string;
}
/**
 * Information about Gallery disk space usage and allowed capacity
 *
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "GalleryCapInfo".
 */
export interface GalleryCapInfo {
    /**
     * Defines if the disk space cap should be enforced or not
     */
    enabled?: boolean;
    /**
     * Gallery disk usage
     */
    usageMB?: number;
    /**
     * Allow disk space capacity when cap is enabled
     */
    capacityPercent?: number;
    /**
     * Total disk space of the drive
     */
    totalDiskSpaceMB?: number;
}
/**
 * Edge Log Event
 *
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "ActivityEvent".
 */
export interface ActivityEvent {
    /**
     * time of the game event
     */
    time: string;
    /**
     * name of the game event
     */
    name: string;
    /**
     * additional information for the event
     */
    info: string;
    /**
     * round number of the event
     */
    round?: number;
}
/**
 * Data for a round score of gameplay
 *
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "ActivityRound".
 */
export interface ActivityRound {
    /**
     * round number
     */
    number: number;
    /**
     * team1 score
     */
    score1?: number;
    /**
     * team2 score
     */
    score2?: number;
    /**
     * start time of the round
     */
    time: string;
}
/**
 * Data for a generated Higlight
 *
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "ActivityData".
 */
export interface ActivityData {
    /**
     * name of the game
     */
    gameName?: string;
    /**
     * session number of the game
     */
    session?: number;
    playStarted?: string;
    playTime?: string;
    /**
     * game start time
     */
    startTime?: string;
    /**
     * game end time
     */
    endTime?: string;
    /**
     * name of filename for map
     */
    mapFileName?: string;
    /**
     * array of edge events
     */
    events?: ActivityEvent[];
    /**
     * array of round end scores
     */
    rounds?: ActivityRound[];
    /**
     * metadata for map files
     */
    metaData?: string;
}
/**
 * Information about Gallery disk space usage and allowed capacity
 *
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "GallerySettings".
 */
export interface GallerySettings {
    /**
     * Fully-quantified path to root of Gallery
     */
    path?: string;
    /**
     * User selected Gallery path, not including PRODUCT subfolder
     */
    userGalleryPath?: string;
    /**
     * Fully-quantified path to ShadowPlay temp folder
     */
    tempFolderPath?: string;
    capacity?: GalleryCapInfo;
    trackerStatus?: GalleryPathUpdateStatus;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "GalleryNotificationsReturn".
 */
export interface GalleryNotificationsReturn {
    /**
     * Indicates Highlights has been saved
     */
    galleryPathUpdateStatus?: {
        status?: GalleryPathUpdateStatus;
    };
    /**
     * Notification to open Highlights summary
     */
    openSummary?: {
        /**
         * Array of Highlights to show in the summary
         */
        ugc?: UGC[];
    };
    /**
     * Highlight Capture timed out
     */
    highlightCanceled?: {
        highlight?: Highlight;
    };
    /**
     * SP failed to capture Highlight
     */
    highlightCaptureFailed?: {
        /**
         * Error code indicating highlight capture failure
         */
        errorCode?: number;
    };
    /**
     * Highlight Capture timed out
     */
    ugcDeleted?: {
        ugc?: UGC;
    };
    /**
     * Capture Saved
     */
    captureSaved?: {
        ugc?: UGC;
        /**
         * Indicates if user needs to be notified on Highlight Captures
         */
        unannounced?: boolean;
    };
    /**
     * Indicates the gallery cap has been reached
     */
    galleryIsFull?: boolean;
    /**
     * Indicates system is running low on disk space
     */
    checkDiskSpace?: {
        /**
         * Indicates if disk space is low
         */
        lowDiskSpace?: boolean;
    };
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "MontageNotificationReturn".
 */
export interface MontageNotificationReturn {
    /**
     * Percentage of montage creation that is complete
     */
    progress?: number;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "PathChangeNotificationReturn".
 */
export interface PathChangeNotificationReturn {
    /**
     * Fully-quantified path of Gallery
     */
    path?: string;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "TempFolderChangeNotificationReturn".
 */
export interface TempFolderChangeNotificationReturn {
    /**
     * Fully-quantified path of temp folder
     */
    path?: string;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "GalleryCapStateChangeNotificationReturn".
 */
export interface GalleryCapStateChangeNotificationReturn {
    capacity?: GalleryCapInfo;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "GalleryUpdatedNotificationReturn".
 */
export interface GalleryUpdatedNotificationReturn {
    capacity?: GalleryCapInfo;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "GetFolderListingParameters".
 */
export interface GetFolderListingParameters {
    /**
     * Name of folder
     */
    directory: string;
    /**
     * Optional, limits NV-based screenshots
     */
    shouldGetOnlyNv?: boolean;
    excludeDirectoryType?: ExcludeDirectoryType;
    shouldShowEXR?: boolean;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "GetFolderListingReturn".
 */
export interface GetFolderListingReturn {
    /**
     * Array of files and their extended information
     */
    files: {
        /**
         * Name of file
         */
        name?: string;
        type?: FileType;
        subtype?: SubType;
        source?: Source;
        /**
         * Fully-quantified path of thumbnail for current file
         */
        thumbnail?: string;
    }[];
    /**
     * Array of subdirectories
     */
    directories: string[];
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "GetAppStatsParameters".
 */
export interface GetAppStatsParameters {
    /**
     * Name of Folder
     */
    appName: string;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "GetAppStatsReturn".
 */
export interface GetAppStatsReturn {
    /**
     * Number of screenshots in the directory
     */
    screenshots?: number;
    /**
     * Number of videos in the directory
     */
    videos?: number;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "GetAppUGCParameters".
 */
export interface GetAppUGCParameters {
    /**
     * Name of Folder
     */
    appName: string;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "GetAppUGCReturn".
 */
export interface GetAppUGCReturn {
    /**
     * Array of Highlights to show in the summary
     */
    ugc: UGC[];
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "RemoveGalleryItemParameters".
 */
export interface RemoveGalleryItemParameters {
    /**
     * Full File/Directory Path
     */
    file: string;
    /**
     * (optional)
     */
    forceDelete?: boolean;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "EnumerateDrivesReturn".
 */
export interface EnumerateDrivesReturn {
    /**
     * Array of system drives
     */
    drives?: {
        /**
         * Name of drive
         */
        name: string;
        /**
         * Type of drive
         */
        type: string;
    }[];
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "GetFileMetadataParameters".
 */
export interface GetFileMetadataParameters {
    /**
     * Full File Path
     */
    file: string;
    /**
     * Desired thumbnail width in px
     */
    width?: number;
    /**
     * Desired thumbnail height in px
     */
    height?: number;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "GetFileMetadataReturn".
 */
export interface GetFileMetadataReturn {
    fileType?: FileType;
    fileSubType?: SubType;
    fileSource?: Source;
    /**
     * DRS Name embedded in the file
     */
    DRSName?: string;
    /**
     * DRS profile embedded in the file
     */
    DRSProfileName?: string;
    /**
     * CMS ID of the app
     */
    cmsId?: number;
    /**
     * Creation date of the file
     */
    date?: string;
    /**
     * Size of file in kilobytes
     */
    fileSizeKB?: number;
    /**
     * Fully-quanitified path to corresponding thumbnail image file
     */
    thumbnail?: string;
    /**
     * Duration of video file
     */
    duration?: string;
    /**
     * Duration of video file in milliseconds
     */
    msDuration?: number;
    /**
     * Timestamp of Highlight Media File
     */
    eventTimeStamp?: string;
    audiotype?: AudioType;
    memeInfo?: MemeInfo;
    /**
     * Codec used for file
     */
    codecType?: string;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "GetEventDataReturn".
 */
export interface GetEventDataReturn {
    /**
     * Array of activity feed data for different games
     */
    recents?: ActivityData[];
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "GetThumbnailParameters".
 */
export interface GetThumbnailParameters {
    /**
     * Full File Path
     */
    file: string;
    /**
     * Desired thumbnail width in px
     */
    width: number;
    /**
     * Desired thumbnail height in px
     */
    height: number;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "GetThumbnailReturn".
 */
export interface GetThumbnailReturn {
    /**
     * Thumbnail of the image file
     */
    thumbnail?: string;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "GetFolderCRCParameters".
 */
export interface GetFolderCRCParameters {
    /**
     * Name of folder
     */
    directory: string;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "GetFolderCRCReturn".
 */
export interface GetFolderCRCReturn {
    /**
     * CRC checksum value for the folder
     */
    crc: number;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "GetImageFileDimensionsParameters".
 */
export interface GetImageFileDimensionsParameters {
    /**
     * Full File Path
     */
    file: string;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "GetImageFileDimensionsReturn".
 */
export interface GetImageFileDimensionsReturn {
    /**
     * Pixel width of the image
     */
    width?: number;
    /**
     * Pixel height of the image
     */
    height?: number;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "CopyFileParameters".
 */
export interface CopyFileParameters {
    /**
     * Full source file path
     */
    source: string;
    /**
     * Full source file path
     */
    destination: string;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "MoveFileParameters".
 */
export interface MoveFileParameters {
    /**
     * Full source file path
     */
    source: string;
    /**
     * Full source file path
     */
    destination: string;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "IsDirectoryWriteableParameters".
 */
export interface IsDirectoryWriteableParameters {
    /**
     * Full directory path
     */
    directory: string;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "IsDirectoryWriteableReturn".
 */
export interface IsDirectoryWriteableReturn {
    writeable?: boolean;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "GetSettingsReturn".
 */
export interface GetSettingsReturn {
    settings: GallerySettings;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "SetSettingsParameters".
 */
export interface SetSettingsParameters {
    settings: GallerySettings;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "GetDriveInfoParameters".
 */
export interface GetDriveInfoParameters {
    /**
     * Designator of drive to gather info about (ex: C or C:)
     */
    drive: string;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "GetDriveInfoReturn".
 */
export interface GetDriveInfoReturn {
    /**
     * Free space on the drive, in megabytes
     */
    freeSpaceMB: number;
    /**
     * Total size of the drive, in megabytes
     */
    totalSpaceMB: number;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "GetRecentUGCParameters".
 */
export interface GetRecentUGCParameters {
    /**
     * Number of ugcs to get
     */
    count: number;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "GetRecentUGCReturn".
 */
export interface GetRecentUGCReturn {
    /**
     * Array of Recent UGC
     */
    recents?: UGC[];
    /**
     * Array of in-flight highlights not yet written to disk
     */
    pending?: UGC[];
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "CheckDiskSpaceReturn".
 */
export interface CheckDiskSpaceReturn {
    /**
     * Indicates if disk space is low
     */
    lowDiskSpace?: boolean;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "EnqueuePendingHighlightParameters".
 */
export interface EnqueuePendingHighlightParameters {
    app?: HighlightAppInfo;
    highlight: Highlight;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "EnqueuePendingHighlightReturn".
 */
export interface EnqueuePendingHighlightReturn {
    highlight: Highlight;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "OpenHighlightsGroupSummaryParameters".
 */
export interface OpenHighlightsGroupSummaryParameters {
    app: HighlightAppInfo;
    /**
     * Array of Groups to show in the summary
     */
    groupsInfo?: HighlightGroupInfo[];
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "GetHighlightsCountParameters".
 */
export interface GetHighlightsCountParameters {
    app: HighlightAppInfo;
    group: HighlightGroupInfo;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "GetHighlightsCountReturn".
 */
export interface GetHighlightsCountReturn {
    /**
     * Highlights count
     */
    count: number;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "DeleteGroupHighlightsParameters".
 */
export interface DeleteGroupHighlightsParameters {
    app: HighlightAppInfo;
    /**
     * Application-sourced group identifier that the Highlight belongs to
     */
    groupId: string;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "WriteEncryptedBitmapParameters".
 */
export interface WriteEncryptedBitmapParameters {
    /**
     * Meme image
     */
    bitmapImage: string;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "TranscodeMediaFileParameters".
 */
export interface TranscodeMediaFileParameters {
    /**
     * Full File Path that is to be transcoded
     */
    file: string;
    /**
     * Full File Path for the output file
     */
    targetPath: string;
    /**
     * User context
     */
    userData?: string;
    /**
     * Max file size
     */
    maxFileSizeMB?: number;
    /**
     * encode quality
     */
    quality?: string;
    /**
     * Duration of the output file
     */
    newDuration?: number;
    /**
     * Desired thumbnail width in px
     */
    newWidth?: number;
    /**
     * Desired thumbnail height in px
     */
    newHeight?: number;
    /**
     * FPS
     */
    newFps?: string;
    /**
     * Meme image
     */
    memeImage?: string;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "TranscodeMediaFileReturn".
 */
export interface TranscodeMediaFileReturn {
    /**
     * User data passed in API parameters
     */
    userData: string;
    /**
     * Full File Path of the output file
     */
    newFile?: string;
    /**
     * File size in bytes of the output file
     */
    newFileSizeB?: number;
    /**
     * Width of the output file in px
     */
    newFileWidth?: number;
    /**
     * Height of the output file in px
     */
    newFileHeight?: number;
    /**
     * Height of the output file in px
     */
    numIterations?: number;
    /**
     * Indicates transcode error
     */
    errorString?: string;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "CreateMontageParameters".
 */
export interface CreateMontageParameters {
    /**
     * Fully-qualified path to input GEMS script file
     */
    script: string;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "CreateMontageFromRecipeParameters".
 */
export interface CreateMontageFromRecipeParameters {
    /**
     * Array of Montage inputs containing pairs of videos and EDGE logs
     */
    inputs: MontageInput[];
    storyboard?: Storyboard;
    /**
     * Name of recipe script file (including extension).  This path will be applied relative to the main montage application data files.
     */
    recipe: string;
    /**
     * Fully-qualified path to name of output montage video to create
     */
    output: string;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "UpdateMontageProgressParameters".
 */
export interface UpdateMontageProgressParameters {
    /**
     * Double from 0.0 to 100.0 indicating current progress
     */
    progress: number;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "TrimParameters".
 */
export interface TrimParameters {
    /**
     * File path of video to trim
     */
    input: string;
    /**
     * File path of trimmed video
     */
    output: string;
    /**
     * number of millisecs to trim from the front
     */
    headTrimMs: number;
    /**
     * length of video in millisecs to from the headTrimMs
     */
    lengthMs: number;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "AddMemeParameters".
 */
export interface AddMemeParameters {
    /**
     * Full File Path
     */
    file: string;
    memeInfo: MemeInfo;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "EngraveMP4Parameters".
 */
export interface EngraveMP4Parameters {
    /**
     * Full File Path
     */
    file: string;
    /**
     * CMS ID of the app
     */
    cmsId?: number;
    /**
     * Profile name of the app
     */
    DRSProfileName?: string;
    /**
     * DRS App name of the app
     */
    DRSAppName?: string;
    /**
     * OPS short name of the app
     */
    DRSShortName?: string;
    /**
     * Indicates if it is a Highlight
     */
    IsAHighlight?: boolean;
    /**
     * Application-sourced definition identifier of the Highlight
     */
    highlightDefinitionId?: string;
    /**
     * Application-sourced group identifier that the Highlight belongs to
     */
    highlightGroupId?: string;
    /**
     * Application-sourced tag values for the Highlight
     */
    highlightTags?: number;
    /**
     * Application-sourced significance values for the Highlight
     */
    highlightSignificance?: number;
    /**
     * Application-sourced group identifier that the Highlight belongs to
     */
    mediaType?: string;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "DeletePreviewParameters".
 */
export interface DeletePreviewParameters {
    /**
     * Path of the preview Image
     */
    previewFilepath: string;
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "PreviewPathInfoParameters".
 */
export interface PreviewPathInfoParameters {
    paths: string[];
}
/**
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "GetMontageCloudConfigReturn".
 */
export interface GetMontageCloudConfigReturn {
    montageConfig?: {
        celebrations?: MontageConfigInfo;
        transitions?: MontageConfigInfo;
        filters?: MontageConfigInfo;
    };
}

/**
 * Type information of file
 *
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "FileType".
 */
export enum FileType {
    video = 'video',
    image = 'image',
    other = 'other'
}
/**
 * Share-oriented subtype of file
 *
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "SubType".
 */
export enum SubType {
    Highlight = 'Highlight',
    AnimatedGif = 'AnimatedGif',
    Normal = 'Normal',
    SuperResolution = 'SuperResolution',
    SuperResolution_Oversized = 'SuperResolution_Oversized',
    SubType_360Mono = '360Mono',
    Stereo = 'Stereo',
    SubType_360Stereo = '360Stereo',
    Exr = 'Exr',
    Normal_Ansel = 'Normal_Ansel',
    Montage = 'Montage',
    InstantReplay = 'InstantReplay',
    Manual = 'Manual',
    Unknown = 'Unknown'
}
/**
 * Indicates Single or Multi-track audio
 *
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "AudioType".
 */
export enum AudioType {
    Single = 'Single',
    Separate = 'Separate',
    Unknown = 'Unknown'
}
/**
 * Quality level
 *
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "Quality".
 */
export enum Quality {
    low = 'low',
    medium = 'medium',
    high = 'high'
}
/**
 * Feature that generated the file
 *
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "Source".
 */
export enum Source {
    GFE_Share = 'GFE_Share',
    GFE_Ansel = 'GFE_Ansel',
    GFE_SDK = 'GFE_SDK',
    StandAlone_Ansel = 'StandAlone_Ansel',
    Undefined = 'Undefined'
}
/**
 * Type of the highlight
 */
export enum Type {
    screenshot = 'screenshot',
    video = 'video'
}
/**
 * Type of Montage input: image, video or audio
 */
export enum InputFileType {
    video = 'video',
    image = 'image',
    audio = 'audio'
}
/**
 * Type of tile sequence
 */
export enum SeqType {
    none = 'none',
    titleBasic = 'titleBasic'
}
/**
 * Type of brand to use
 */
export enum BrandType {
    none = 'none',
    brandSliderInOnly = 'brandSliderInOnly',
    brandSlideInOut = 'brandSlideInOut'
}
/**
 * Position of brand within story board
 */
export enum Position {
    atStart = 'atStart',
    atEnd = 'atEnd'
}
/**
 * Tracker update status
 *
 * This interface was referenced by `Gallery`'s JSON-Schema
 * via the `definition` "GalleryPathUpdateStatus".
 */
export enum GalleryPathUpdateStatus {
    TrackerUpdateNotActive = 'TrackerUpdateNotActive',
    TrackerUpdateInProgress = 'TrackerUpdateInProgress',
    TrackerUpdateComplete = 'TrackerUpdateComplete',
    CancelUpdateComplete = 'CancelUpdateComplete',
    CancelUpdate = 'CancelUpdate'
}
export enum ExcludeDirectoryType {
    none = 'none',
    hidden = 'hidden',
    empty = 'empty',
    hiddenOrEmpty = 'hiddenOrEmpty',
    hiddenAndEmpty = 'hiddenAndEmpty'
}


}
